import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalContext } from "@/context/global/GlobalContext";
import { normalizeTerm } from "./search-utils";
import {
  activateDesktopSearchNavigationItem,
  buildDesktopSearchNavigationItems,
  isHighConfidencePlaceMatch,
} from "./search-keyboard-navigation";
import { useSearchActions } from "./use-search-actions";
import { useSearchTerms } from "./use-search-terms";
import { usePlaceSuggestions } from "./use-place-suggestions";
import { usePopularSearches } from "./use-popular-searches";
import { useSearchOverlayLifecycle } from "./use-search-overlay-lifecycle";

const getPlaceRouteParams = (place) => ({
  placeId: place?.id || place?._id || "",
  placeUniqueNumber: place?.placeUniqueNumber || "",
});

const getPlaceLinkedPostCount = (place) =>
  Number(place?.linkedPostCount ?? place?.postCount ?? 0) || 0;

const isExactPlaceMatch = (query, place) =>
  Boolean(place?.exactPlaceMatch) || isHighConfidencePlaceMatch(query, place);

export const useSearchInputController = ({
  mobileOverlayVariant = "default",
  autoOpenMobile = false,
  onMobileOverlayClose,
} = {}) => {
  const searchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const keyRef = useRef("search-history-menu");
  const {
    search,
    setSearch,
    searchInFocus,
    setSearchInFocus,
    searchHistory,
    frequentSearches,
    searchRelatedTerms,
    addToSearchHistory,
    trendingPostTerms,
  } = useGlobalContext();
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
  const openSuggestionRefreshKeyRef = useRef({ key: "", attempts: 0 });
  const shouldPortalMobileOverlay =
    ["main", "play"].includes(mobileOverlayVariant) &&
    typeof document !== "undefined";

  const trimmedSearch = normalizeTerm(search);
  const {
    nearbyOnly,
    handleNearbyOnlyChange,
    userLocation,
    topPlace,
    placeSuggestions,
    isSuggestionLoading,
    fetchPlaceSuggestions,
  } = usePlaceSuggestions({ trimmedSearch });
  const topPlaceRef = useRef(null);
  const { popularItems, popularUpdatedAt } = usePopularSearches({
    searchInFocus,
    mobileOverlayOpen,
    frequentSearches,
    trendingPostTerms,
  });
  const { isDesktopViewport } = useSearchOverlayLifecycle({
    searchRef,
    mobileSearchInputRef,
    keyRef,
    mobileOverlayOpen,
    autoOpenMobile,
    setSearch,
    setSearchInFocus,
    setMobileOverlayOpen,
  });
  const { routeToSearch, routeToPlace, openSearch, closeSearch } =
    useSearchActions({
      trimmedSearch,
      setSearch,
      setSearchInFocus,
      setMobileOverlayOpen,
      addToSearchHistory,
      onMobileOverlayClose,
    });

  useEffect(() => {
    topPlaceRef.current = topPlace || null;
  }, [topPlace]);

  useEffect(() => {
    const searchSurfaceOpen = searchInFocus || mobileOverlayOpen;
    if (!searchSurfaceOpen) {
      openSuggestionRefreshKeyRef.current = { key: "", attempts: 0 };
      return;
    }

    if (
      trimmedSearch.length < 2 ||
      isSuggestionLoading ||
      placeSuggestions.length > 0
    ) {
      return;
    }

    const refreshKey = [
      trimmedSearch,
      nearbyOnly ? "nearby" : "all",
      userLocation?.lat || "",
      userLocation?.lng || "",
    ].join("|");
    const recentRefresh = openSuggestionRefreshKeyRef.current;

    if (recentRefresh.key !== refreshKey) {
      openSuggestionRefreshKeyRef.current = { key: refreshKey, attempts: 0 };
    } else if (recentRefresh.attempts >= 2) {
      return;
    }

    openSuggestionRefreshKeyRef.current = {
      key: refreshKey,
      attempts: openSuggestionRefreshKeyRef.current.attempts + 1,
    };
    fetchPlaceSuggestions(trimmedSearch);
  }, [
    fetchPlaceSuggestions,
    isSuggestionLoading,
    mobileOverlayOpen,
    nearbyOnly,
    placeSuggestions.length,
    searchInFocus,
    trimmedSearch,
    userLocation?.lat,
    userLocation?.lng,
  ]);

  const waitForExactTopPlace = useCallback((query, timeoutMs = 650) => {
    const startedAt = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        const candidate = topPlaceRef.current;

        if (candidate && isExactPlaceMatch(query, candidate)) {
          resolve(candidate);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          resolve(null);
          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  }, []);

  const routeByExactPlace = useCallback(
    (query, place) => {
      if (!place || !isExactPlaceMatch(query, place)) return false;

      if (getPlaceLinkedPostCount(place) > 0) {
        routeToSearch(query, getPlaceRouteParams(place));
      } else {
        routeToPlace(place);
      }

      return true;
    },
    [routeToPlace, routeToSearch]
  );

  const routeToBestSearchTarget = useCallback(
    async (query) => {
      const nextSearch = normalizeTerm(query);
      if (!nextSearch) return;

      if (routeByExactPlace(nextSearch, topPlace)) {
        return;
      }

      const pendingTopPlace = await waitForExactTopPlace(nextSearch);
      if (routeByExactPlace(nextSearch, pendingTopPlace)) {
        return;
      }

      const suggestions = await fetchPlaceSuggestions(nextSearch, {
        updateState: false,
        includeExecutionMeta: true,
      });
      const bestPlace =
        suggestions?.find((place) => isExactPlaceMatch(nextSearch, place)) ||
        suggestions?.[0];

      if (routeByExactPlace(nextSearch, bestPlace)) {
        return;
      }

      routeToSearch(nextSearch);
    },
    [fetchPlaceSuggestions, routeByExactPlace, topPlace, waitForExactTopPlace]
  );
  const { matchedRecentTerms, suggestionTerms } = useSearchTerms({
    search,
    searchInFocus,
    mobileOverlayOpen,
    searchHistory,
    searchRelatedTerms,
    trendingPostTerms,
    frequentSearches,
    trimmedSearch,
  });
  const [desktopActiveSearchIndex, setDesktopActiveSearchIndex] = useState(-1);
  const desktopSearchNavigationItems = useMemo(
    () =>
      buildDesktopSearchNavigationItems({
        trimmedSearch,
        popularItems,
        placeSuggestions,
        matchedRecentTerms,
        suggestionTerms,
        isSuggestionLoading,
      }),
    [
      trimmedSearch,
      popularItems,
      placeSuggestions,
      matchedRecentTerms,
      suggestionTerms,
      isSuggestionLoading,
    ]
  );
  const desktopSearchNavigationSignature = useMemo(
    () => desktopSearchNavigationItems.map((item) => item.key).join("|"),
    [desktopSearchNavigationItems]
  );

  useEffect(() => {
    setDesktopActiveSearchIndex(-1);
  }, [desktopSearchNavigationSignature, searchInFocus, mobileOverlayOpen]);

  useEffect(() => {
    if (desktopActiveSearchIndex >= desktopSearchNavigationItems.length) {
      setDesktopActiveSearchIndex(desktopSearchNavigationItems.length - 1);
    }
  }, [desktopActiveSearchIndex, desktopSearchNavigationItems.length]);

  const activateDesktopSearchItem = useCallback(
    (item) =>
      activateDesktopSearchNavigationItem(item, {
        routeToSearch,
        routeToBestSearchTarget,
        routeToPlace,
      }),
    [routeToBestSearchTarget, routeToPlace, routeToSearch]
  );

  const handleDesktopSearchKeyDown = useCallback(
    (event) => {
      if (!searchInFocus || !isDesktopViewport) return;

      const itemCount = desktopSearchNavigationItems.length;

      if (event.key === "ArrowDown") {
        if (!itemCount) return;
        event.preventDefault();
        setDesktopActiveSearchIndex((currentIndex) =>
          currentIndex < 0 ? 0 : (currentIndex + 1) % itemCount
        );
        return;
      }

      if (event.key === "ArrowUp") {
        if (!itemCount) return;
        event.preventDefault();
        setDesktopActiveSearchIndex((currentIndex) =>
          currentIndex < 0
            ? itemCount - 1
            : (currentIndex - 1 + itemCount) % itemCount
        );
        return;
      }

      if (event.key === "Enter" && desktopActiveSearchIndex >= 0) {
        const activeItem = desktopSearchNavigationItems[desktopActiveSearchIndex];
        if (activateDesktopSearchItem(activeItem)) {
          event.preventDefault();
        }
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        const inputValue =
          event.currentTarget?.querySelector?.("input")?.value || search;
        routeToBestSearchTarget(inputValue);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        closeSearch();
      }
    },
    [
      activateDesktopSearchItem,
      closeSearch,
      desktopActiveSearchIndex,
      desktopSearchNavigationItems,
      isDesktopViewport,
      routeToBestSearchTarget,
      search,
      searchInFocus,
    ]
  );

  const setDesktopActiveSearchKey = useCallback(
    (key) => {
      const nextIndex = desktopSearchNavigationItems.findIndex(
        (item) => item.key === key
      );
      setDesktopActiveSearchIndex(nextIndex);
    },
    [desktopSearchNavigationItems]
  );

  const refs = {
    searchRef,
    mobileSearchInputRef,
    keyRef,
  };
  const searchState = {
    search,
    setSearch,
    searchInFocus,
  };
  const viewportState = {
    mobileOverlayOpen,
    shouldPortalMobileOverlay,
    isDesktopViewport,
  };
  const suggestionState = {
    nearbyOnly,
    setNearbyOnly: handleNearbyOnlyChange,
    userLocation,
    isSuggestionLoading,
    popularItems,
    popularUpdatedAt,
    topPlace,
    placeSuggestions,
    matchedRecentTerms,
    suggestionTerms,
  };
  const searchActions = {
    routeToSearch,
    routeToBestSearchTarget,
    routeToPlace,
    openSearch,
    closeSearch,
  };
  const desktopNavigationState = {
    activeIndex: desktopActiveSearchIndex,
    activeKey:
      desktopActiveSearchIndex >= 0
        ? desktopSearchNavigationItems[desktopActiveSearchIndex]?.key
        : "",
    items: desktopSearchNavigationItems,
    onKeyDown: handleDesktopSearchKeyDown,
    setActiveIndex: setDesktopActiveSearchIndex,
    setActiveKey: setDesktopActiveSearchKey,
  };

  return {
    refs,
    searchState,
    viewportState,
    suggestionState,
    searchActions,
    desktopNavigationState,
  };
};
